import { useState } from 'react';

import { NextPageWithLayout } from 'next';
import { useForm } from 'react-hook-form';
import get from 'ts-get';

import { useRutilusMutation } from '../../api/graphql/useRutilusMutation';
import { useRutilusQuery } from '../../api/graphql/useRutilusQuery';
import { SaveButton } from '../../components/Clickables/Buttons/SaveButton';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { MAIN_NAVIGATION_MIN_HEIGHT } from '../../components/Navigation/MainNavigation/constants/constants';
import { Text } from '../../components/Text/Text';
import { reportMykissError } from '../../helpers/errorHandling';
import { useClientHasRendered } from '../../helpers/hooks';
import { useImageUpload } from '../../helpers/hooks/useImageUpload';
import { scrollToRef } from '../../helpers/scroll/scrollToRef';
import {
  CreateUserSuggestedChangeMutation,
  CreateUserSuggestedChangeMutationInput,
  CreateUserSuggestedChangeMutationVariables,
  GetFishingWaterMetadata,
  GetFishingWaterMetadataVariables,
} from '../../interfaces/graphql';
import { createUserSuggestedChangeMutation } from '../../mutations/rutilus/CreateUserSuggestedChange';
import { getFishingWaterMetadata } from '../../queries/rutilus/FishingWaters';

import { NoChangesModal } from './components/Modals/NoChangesModal';
import { SuggestionError } from './components/SuggestionError';
import { SuggestionFields } from './components/SuggestionFields';
import { SuggestionSuccess } from './components/SuggestionSuccess';
import {
  getLocationSuggestion,
  getMetadataSuggestion,
  getNewMetadataSuggestion,
  getUrlSuggestion,
  getWaterNameSuggestion,
  hasSuggestion,
} from './helpers/suggestionValidation';
import { uploadFiles } from './helpers/uploadFiles';
import { FishingWater } from './types/FishingWater';
import { SuggestedWaterFormState } from './types/SuggestedWaterForm';

export interface ISuggestWaterChanges {
  source?: 'app' | 'web';
  fishingWater?: FishingWater;
  isNewForm?: boolean;
}

export const SuggestWaterChanges: NextPageWithLayout<ISuggestWaterChanges> = ({
  source = 'web',
  fishingWater,
  isNewForm = false,
}) => {
  const [state, setState] = useState<'presubmit' | 'submitting' | 'submitted' | 'errorSubmit'>(
    'presubmit',
  );
  const [isNoChangesModalOpen, setIsNoChangesModalOpen] = useState(false);
  const isNotAppPage = source === 'web';
  const clientHasRendered = useClientHasRendered();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
  } = useForm<SuggestedWaterFormState>();

  const { data: allMetadata, loading: isLoadingMetaData } = useRutilusQuery<
    GetFishingWaterMetadata,
    GetFishingWaterMetadataVariables
  >(getFishingWaterMetadata);

  const [createUserSuggestedChange] = useRutilusMutation<
    CreateUserSuggestedChangeMutation,
    CreateUserSuggestedChangeMutationVariables
  >(createUserSuggestedChangeMutation);

  const uploadFile = useImageUpload();

  const onSubmit = async (formState: SuggestedWaterFormState): Promise<void> => {
    if (
      !isNewForm &&
      fishingWater &&
      !hasSuggestion({ formData: formState, fishingwater: fishingWater })
    ) {
      setIsNoChangesModalOpen(true);
      return;
    }

    setState('submitting');
    try {
      const attributes = {
        newWaterName: isNewForm
          ? formState.newWaterName
          : getWaterNameSuggestion(fishingWater?.name || '', formState.newWaterName),
        position: isNewForm
          ? formState.position
          : getLocationSuggestion(
              fishingWater?.coordinates || { latitude: 0, longitude: 0 },
              formState.position,
            ),
        fishingWaterMetadataExternalIds: isNewForm
          ? getNewMetadataSuggestion(formState.fishingWaterMetadata)
          : getMetadataSuggestion(fishingWater?.metadata || [], formState.fishingWaterMetadata),
        url: isNewForm
          ? formState.url || undefined
          : getUrlSuggestion(fishingWater?.url || '', formState.url),
        attachments: formState.attachments
          ? await uploadFiles(formState.attachments, uploadFile)
          : undefined,
        fishingWaterExternalId: fishingWater?.externalId,
        additionalInformation:
          formState.additionalInformation !== '' ? formState.additionalInformation : undefined,
      };
      const input: CreateUserSuggestedChangeMutationInput = {
        attributes,
      };

      const result = await createUserSuggestedChange({
        variables: { input },
      });
      const userErrors = get(result, r => r.data.createUserSuggestedChange.userErrors, []);
      if (userErrors.length) {
        throw new Error(userErrors[0].message);
      }
      setState('submitted');
    } catch (error) {
      setState('errorSubmit');
      scrollToRef();
      reportMykissError(error);
    }
    scrollToRef();
  };

  const handleGoBack = (): void => {
    setState('presubmit');
    scrollToRef();
  };

  if (state === 'submitted') {
    return (
      <div className="relative m-auto flex max-w-(--onboarding-flow-plus-width) flex-col px-6 pb-6">
        <MetaTags title="Suggest changes" robots="noindex, nofollow" />
        <SuggestionSuccess
          isNotAppPage={isNotAppPage}
          fishingWater={fishingWater ?? { externalId: '', name: '' }}
          isNewForm={isNewForm}
        />
      </div>
    );
  }

  if (state === 'errorSubmit') {
    return (
      <div className="relative m-auto flex max-w-(--onboarding-flow-plus-width) flex-col px-6 pb-6">
        <MetaTags title="Suggest changes" robots="noindex, nofollow" />
        <SuggestionError onNavigateBack={handleGoBack} isNotAppPage={isNotAppPage} />
      </div>
    );
  }

  return (
    <div className="relative m-auto flex max-w-(--onboarding-flow-plus-width) flex-col px-6 pb-6">
      <MetaTags
        title={
          isNewForm
            ? 'Suggest changes for a fishing water'
            : `Suggest changes for ${fishingWater?.name}`
        }
        robots="noindex, nofollow"
      />
      {clientHasRendered && isNotAppPage && (
        <>
          <header
            className={`top-[${MAIN_NAVIGATION_MIN_HEIGHT}px] z-high sticky w-full bg-white pt-4 sm:pt-8 md:pt-12`}
          >
            <Heading variant="sm" className="mt-0 mb-1" as="h1">
              Suggest changes
            </Heading>
            {!isNewForm && (
              <Text variant="sm" className="mb-4">
                {fishingWater?.name}
              </Text>
            )}
          </header>
        </>
      )}

      <div>
        <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
          <SuggestionFields
            register={register}
            control={control}
            setValue={setValue}
            errors={errors}
            watch={watch}
            allMetadata={allMetadata}
            isLoadingMetaData={isLoadingMetaData}
            isNewForm={isNewForm}
            existingData={fishingWater}
            requiredFields={isNewForm ? ['newWaterName', 'position'] : []}
          />
          {clientHasRendered && (
            <div className="my-2">
              <SaveButton
                isSaving={state === 'submitting'}
                type="submit"
                className="mt-auto mb-0 ml-auto w-full"
                savingLabel="Submitting..."
                disabled={isLoadingMetaData}
              >
                Submit changes
              </SaveButton>
            </div>
          )}
        </form>
        {isNoChangesModalOpen && (
          <NoChangesModal isOpen={isNoChangesModalOpen} setIsOpen={setIsNoChangesModalOpen} />
        )}
      </div>
    </div>
  );
};
